import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { catchFunction, API } from '../utils';
import { NumberRef } from './hooks.types';
import { PostTypes } from './../types';

interface usePostsTypes {
    url: string;
    limit?: number;
}

export const usePosts = ({ url, limit = 5 }: usePostsTypes) => {
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [posts, setPosts] = useState<PostTypes[] | []>([]);
    const [total, setTotal] = useState<NumberRef>(null);
    const [prevY, setPrevY] = useState(0);
    const [fullPost, setFullPost] = useState(false);

    const postsRef = useRef<PostTypes[] | null>(null);
    const prevYRef = useRef<NumberRef>(null);
    const totalRef = useRef<NumberRef>(null);
    const pageRef = useRef<NumberRef>(null);
    const loadingRef = useRef<HTMLDivElement | null>(null);

    prevYRef.current = prevY;
    totalRef.current = total;
    pageRef.current = page;
    postsRef.current = posts;

    const getData = useCallback(
        async (signal: AbortSignal) => {
            try {
                const { data } = await API.get(url, {
                    params: { page, limit },
                    signal,
                });
                if (data?.posts?.length !== 0) {
                    setPosts(postsRef?.current ? [...postsRef.current, ...data.posts] : data.posts);
                    setTotal(data?.totalItems);
                }
            } catch (error) {
                catchFunction(error);
            } finally {
                setLoading(false);
            }
        },
        [page, limit, url],
    );
    useEffect(() => {
        if (posts?.length > 0) {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 1.0,
            };

            const observer = new IntersectionObserver((ent) => {
                const y = ent && ent[0]?.boundingClientRect?.y;
                if (prevYRef.current === null || totalRef.current === null || pageRef.current === null) {
                    return;
                }
                if (prevYRef.current > y && totalRef.current === limit) {
                    setPage(pageRef.current + 1);
                }
                if (totalRef.current < limit) {
                    setFullPost(true);
                }
                setPrevY(y);
            }, options);
            if (loadingRef.current === null) {
                return;
            }
            observer.observe(loadingRef.current);
        }
    }, [loadingRef, posts, limit, totalRef]);
    useLayoutEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        getData(signal);
        return () => controller.abort();
    }, [getData]);
    return { posts, loading, fullPost, loadingRef };
};
